import React, { createContext, useContext, useState, useEffect } from 'react';
import Logger from '../Services/loggerService';
import apiClient, { setAccessToken } from '../Services/apiClient';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth debe ser usado dentro de un AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        
        const checkAuth = async () => {
            // Si hay un token en la URL o un proceso SSO activo, esperar
            const ssoInProgress = sessionStorage.getItem('sso_in_progress');

            // En las rutas de login no se debe intentar refresh-token sin sesiÃ³n.
            if (window.location.pathname === '/sso' || window.location.pathname === '/login') {
                setIsAuthenticated(false);
                setUser(null);
                setIsLoading(false);
                return;
            }
            
            if (urlParams.get('token') || ssoInProgress) {
                Logger.auth('🚦 [AuthContext] SSO en progreso detectado, esperando...');
                return;
            }

            try {
                // Primero verificar si hay un token en cookies
                const Cookies = (await import('js-cookie')).default;
                const tokenFromCookie = Cookies.get('token');
                const userRole = Cookies.get('userRole');
                const nit = Cookies.get('nit');

                // Si hay token en cookies, verificar si está expirado
                if (tokenFromCookie) {
                    try {
                        const payload = JSON.parse(atob(tokenFromCookie.split('.')[1]));
                        const isExpired = payload.exp * 1000 < Date.now();

                        if (!isExpired) {
                            // Token válido en cookies, usarlo
                            setAccessToken(tokenFromCookie);
                            setIsAuthenticated(true);
                            if (nit && userRole) {
                                setUser({
                                    nit: nit,
                                    role: userRole,
                                    razonSocial: Cookies.get('razonSocial') || null
                                });
                            }
                            setIsLoading(false);
                            return; // Salir temprano si tenemos token válido
                        }
                    } catch (e) {
                        // Si no se puede parsear el token, continuar con refresh-token
                    }
                }

                // Si no hay token válido en cookies, intentar refresh-token
                const apiBaseUrl = import.meta.env.VITE_API_URL || '/api';
                const res = await fetch(`${apiBaseUrl}/auth/refresh-token`, {
                    method: 'POST',
                    credentials: 'include'
                });
                if (res.ok) {
                    const data = await res.json();
                    if (data?.token) {
                        setAccessToken(data.token);
                        setIsAuthenticated(true);
                        // El usuario se establecerá después del login o al cargar el perfil
                    }
                } else if (res.status === 401) {
                    // 401 es esperado cuando no hay sesión activa
                    // Si no hay token en cookies tampoco, entonces no hay sesión
                    if (!tokenFromCookie) {
                        setIsAuthenticated(false);
                        setUser(null);
                    } else {
                        // Hay token en cookies pero refresh falló, mantener autenticación con token de cookies
                        setAccessToken(tokenFromCookie);
                        setIsAuthenticated(true);
                        if (nit && userRole) {
                            setUser({
                                nit: nit,
                                role: userRole,
                                razonSocial: Cookies.get('razonSocial') || null
                            });
                        }
                    }
                }
            } catch (error) {
                // Si es un error 429 (Too Many Requests), no limpiar la sesión
                // Esto permite que el usuario se mantenga logueado si el servidor está temporalmente saturado
                if (error.response?.status === 429) {
                    Logger.warn('🚦 [AuthContext] Backend saturado (429), manteniendo estado actual');
                    setIsLoading(false);
                    return;
                }

                // Solo loggear errores de red, no errores 401 esperados
                if (error.name !== 'TypeError' || !error.message.includes('fetch')) {
                    Logger.error('Error al verificar autenticación:', error);
                }

                // Como último recurso, verificar si hay token en cookies
                try {
                    const Cookies = (await import('js-cookie')).default;
                    const tokenFromCookie = Cookies.get('token');
                    const userRole = Cookies.get('userRole');
                    const nit = Cookies.get('nit');

                    if (tokenFromCookie && nit && userRole) {
                        try {
                            const payload = JSON.parse(atob(tokenFromCookie.split('.')[1]));
                            const isExpired = payload.exp * 1000 < Date.now();

                            if (!isExpired) {
                                setAccessToken(tokenFromCookie);
                                setIsAuthenticated(true);
                                setUser({
                                    nit: nit,
                                    role: userRole,
                                    razonSocial: Cookies.get('razonSocial') || null
                                });
                            } else {
                                setIsAuthenticated(false);
                                setUser(null);
                            }
                        } catch (e) {
                            setIsAuthenticated(false);
                            setUser(null);
                        }
                    } else {
                        setIsAuthenticated(false);
                        setUser(null);
                    }
                } catch (cookieError) {
                    setIsAuthenticated(false);
                    setUser(null);
                }
            } finally {
                setIsLoading(false);
            }
        };
        
        // Antes de finalizar, verificar si hay un proceso SSO en curso en App.jsx
        // para no quitar el loader prematuramente y causar ráfagas de peticiones
        const ssoInProgress = sessionStorage.getItem('sso_in_progress');
        if (ssoInProgress === 'true' && (urlParams.get('token') || window.location.search.includes('token='))) {
            Logger.auth('🚦 [AuthContext] Manteniendo estado de carga por proceso SSO activo');
            return; // No poner isLoading(false) aún
        }

        checkAuth();
    }, []);

    const login = async (userData) => {
        try {
            console.log('[AuthContext] Datos recibidos en login:', userData);

            // Guardar access token solo en memoria
            setAccessToken(userData.token);

            // Actualizar estado
            setIsAuthenticated(true);
            setUser({
                nit: userData.nit,
                razonSocial: userData.razonSocial,
                role: userData.role,
                hasProfilePicture: userData.hasProfilePicture,
                imageUrl: userData.imageUrl
            });

            // Disparar evento de login exitoso con información de imagen de perfil
            console.log('[AuthContext] Disparando evento loginSuccess con datos:', {
                nit: userData.nit,
                hasProfilePicture: userData.hasProfilePicture,
                imageUrl: userData.imageUrl
            });

            window.dispatchEvent(new CustomEvent('loginSuccess', {
                detail: {
                    nit: userData.nit,
                    token: userData.token, // <<< AÑADIR TOKEN AQUÍ
                    razonSocial: userData.razonSocial,
                    role: userData.role,
                    hasProfilePicture: userData.hasProfilePicture,
                    imageUrl: userData.imageUrl
                }
            }));

            return true;
        } catch (error) {
            Logger.error('Error en login:', error);
            return false;
        }
    };

    const logout = async () => {
        try {
            // Llamar logout del backend para limpiar cookie HttpOnly
            await fetch(`${import.meta.env.VITE_API_URL}/auth/logout`, {
                method: 'POST',
                credentials: 'include'
            });
            setAccessToken(null);

            // Limpiar estado
            setIsAuthenticated(false);
            setUser(null);

            // Disparar evento de logout
            window.dispatchEvent(new CustomEvent('logout'));

            return true;
        } catch (error) {
            Logger.error('Error en logout:', error);
            return false;
        }
    };

    const value = {
        isAuthenticated,
        user,
        isLoading,
        login,
        logout
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext; 
